import { useState, useEffect, useCallback, useRef, Children } from 'react';
import { debounce } from 'underscore';
import classNames from 'classnames';

import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { Session, SessionPreview } from '../../../../types/working-model';
import Icon, { ICONS } from '../../../general-ui/icon';
import { noTrackName } from './agenda-utils';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../hooks/use-translation-route';
import AssignTracks from '../../../admin/create/homepage/editor/agenda-editor/assign-tracks/assign-tracks';
import { ConditionalWrapper } from '../../../../utils/conditional-wrapper';

interface AgendaCarouselProps {
	track: string;
	group: (Session | SessionPreview | undefined)[]
	carouselControls?: React.ReactNode;
	children: React.ReactNode;
	displayHeaderText?: boolean;
	isResponsive?: boolean;
	isEditor?: boolean;
	setBlockClose?: React.Dispatch<React.SetStateAction<boolean>>;
}

const PROGRESS_BAR_SLIDER_WIDTH = 46; // value from figma
const PROGRESS_BAR_SLIDER_MARKER_MIN_WIDTH = 5;

export const AgendaCarousel = ({
	track,
	group,
	carouselControls,
	children,
	displayHeaderText = true,
	isResponsive = true,
	isEditor = false,
	setBlockClose,
}: AgendaCarouselProps): JSX.Element => {
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);

	const [sliderProgressPage, setSliderProgressPage] = useState(0);
	const [itemsPerScreen, setItemsPerScreen] = useState(1);
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(['homepage', translationRoute, 'session']);
	const carouselRef = useRef<HTMLDivElement | null>(null);
	const ondemandCarouselContainer = useRef<HTMLDivElement | null>(null);
	const leftHandleRef = useRef<HTMLButtonElement | null>(null);
	const rightHandleRef = useRef<HTMLButtonElement | null>(null);
	const rightOverflowShadow = useRef<HTMLDivElement | null>(null);
	const leftOverflowShadow = useRef<HTMLDivElement | null>(null);
	const progressBarSliderRef = useRef<HTMLDivElement | null>(null);
	const progressBarMarkerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		setItemsPerScreen(carouselRef.current
			? parseInt(
				window.getComputedStyle(carouselRef.current).getPropertyValue('--items-per-screen')
			) : 1);
	}, [carouselRef]);

	const progressBarPages = itemsPerScreen > 0 ? Math.ceil(group.length / itemsPerScreen) : 0;
	const isUntracked = track === noTrackName;

	const updateShowHandles = useCallback(() => {
		if (!leftOverflowShadow.current || !leftHandleRef.current || !rightOverflowShadow.current || !rightHandleRef.current) return;

		const showHandle = group.length > itemsPerScreen;
		const isFirstPage = sliderProgressPage === 0;
		const isLastPage = (sliderProgressPage + 1) * itemsPerScreen >= Children.count(children);

		leftOverflowShadow.current.style.display = showHandle ? "flex" : "none";
		leftHandleRef.current.style.display = showHandle && !isFirstPage ? "flex" : "none";
		rightOverflowShadow.current.style.display = showHandle ? "flex" : "none";
		rightHandleRef.current.style.display = showHandle && !isLastPage ? "flex" : "none";

	}, [group.length, itemsPerScreen, sliderProgressPage, children]);

	const updateSliderOffset = useCallback(() => {
		// Controls the sliding animation and transform.
		const selectedSlider = ondemandCarouselContainer?.current?.querySelector('.slider') as HTMLElement;
		if (!selectedSlider) return;

		const cards = selectedSlider.querySelectorAll<HTMLElement>('.ondemand-carousel-card');
		const gapWidth = 10;
		const sliderIndex = parseInt(selectedSlider.dataset.sliderIndex ?? '0');
		const offset = sliderIndex * -(selectedSlider.offsetWidth + gapWidth);

		cards.forEach((card) => {
			card.style.transform = `translateX(${offset}px)`;
		});
	}, []);

	const updateHandleVerticalOffset = useCallback(() => {
		// Timer waits for css to finish
		// TODO redo card and slider layouts to use pure css alignment
		setTimeout(() => {
			if (!leftHandleRef.current || !rightHandleRef.current || !carouselRef.current) return;

			const height = carouselRef.current.querySelector('.ondemand-carousel-card img')?.clientHeight ?? 0;
			const offset = `${height / 2 - 24}px`;

			leftHandleRef.current.style.top = offset;
			rightHandleRef.current.style.top = offset;
		}, 750);
	}, []);

	const updateShadowWidth = useCallback(() => {
		if (!rightOverflowShadow.current || !leftOverflowShadow.current) return;

		const visibleCarouselWidth = ondemandCarouselContainer.current?.clientWidth;
		const calendarWidth = carouselRef.current?.closest('.agenda-calendar')?.clientWidth;
		if (!visibleCarouselWidth || !calendarWidth) return;

		const availableHandleSpace = (calendarWidth - visibleCarouselWidth) / 2;
		const offset = `${-1 * availableHandleSpace}px`;
		const width = `${availableHandleSpace}px`;

		rightOverflowShadow.current.style.right = offset;
		rightOverflowShadow.current.style.width = width;

		leftOverflowShadow.current.style.left = offset;
		leftOverflowShadow.current.style.width = width;
	}, []);

	const generatePageProgressBar = useCallback(() => {
		if (!progressBarMarkerRef.current || !progressBarSliderRef.current) return;

		const pages = Math.ceil(group.length / itemsPerScreen);

		progressBarSliderRef.current.style.display = pages === 1 ? "none" : "flex";

		const realMarkerWidth = Math.ceil((1 / pages) * PROGRESS_BAR_SLIDER_WIDTH);
		const markerWidth = realMarkerWidth >= PROGRESS_BAR_SLIDER_MARKER_MIN_WIDTH ? realMarkerWidth : PROGRESS_BAR_SLIDER_MARKER_MIN_WIDTH;

		const marginLeft = realMarkerWidth * sliderProgressPage;

		progressBarMarkerRef.current.style.width = `${markerWidth}px`;
		progressBarMarkerRef.current.style.marginLeft = `${marginLeft}px`;

	}, [group.length, itemsPerScreen, sliderProgressPage]);

	// Set the correct offset on every render
	updateSliderOffset();

	// Vertically align handles on every render
	updateHandleVerticalOffset();

	// Update shadow styling on every render
	updateShadowWidth();

	// Generate the progress bar changes when the screen changes.
	generatePageProgressBar();

	// Update the handles on screen size to show or not show.
	updateShowHandles();

	useEffect(() => {
		// Resize fires continuously
		const handleResize = debounce(() => {
			updateSliderOffset();
			updateHandleVerticalOffset();
			updateShadowWidth();
			generatePageProgressBar();
			updateShowHandles();
			setItemsPerScreen(carouselRef.current
				? parseInt(
					window.getComputedStyle(carouselRef.current).getPropertyValue('--items-per-screen')
				) : 1);
		}, 100);
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [updateHandleVerticalOffset, updateShadowWidth, updateSliderOffset, generatePageProgressBar, updateShowHandles]);

	interface ButtonInterface {
		target: HTMLButtonElement;
	}

	function onHandleClick(_handle: ButtonInterface) {
		let handle;

		// Sets the element depending on where you clicked
		if (_handle?.target?.matches(".handle")) {
			handle = _handle.target;
		} else {
			handle = _handle?.target?.closest(".handle");
		}

		if (!handle) return;
		const slider = handle.closest(".ondemand-carousel-container")?.querySelector(".slider");

		// More Typescript shenanigans
		if (!slider) return;
		if (!(slider instanceof HTMLDivElement)) return;

		// How many 'pages' are there and which page are you on.
		const sliderIndex = parseInt(slider.dataset.sliderIndex || '0');

		// Click the left chevron
		if (handle.classList.contains("left-handle")) {
			if (sliderIndex - 1 < 0) {
				slider.dataset.sliderIndex = `${(progressBarPages ?? 1) - 1}`;
				setSliderProgressPage(parseInt(slider.dataset.sliderIndex));
			} else {
				slider.dataset.sliderIndex = `${sliderIndex - 1}`;
				setSliderProgressPage(parseInt(slider.dataset.sliderIndex));
			}
		}

		// Click the right chevron
		if (handle.classList.contains("right-handle")) {
			if (sliderIndex + 1 >= (progressBarPages ?? 1)) {
				slider.dataset.sliderIndex = '0';
				setSliderProgressPage(parseInt(slider.dataset.sliderIndex));
			} else {
				slider.dataset.sliderIndex = `${sliderIndex + 1}`;
				setSliderProgressPage(parseInt(slider.dataset.sliderIndex));
			}
		}
	}

	return (
		<div
			className={classNames({
				'carousel-responsive': isResponsive,
				[`carousel-${editorSize}`]: !isResponsive,
			})}
		>
			<div ref={carouselRef} key={track} className="ondemand-carousel-wrapper">
				{displayHeaderText && (
					<div className="carousel-header evt-typography-h6">
						{isUntracked ? (
							<ConditionalWrapper condition={isEditor} wrapper={children => <div className="carousel-header-editor-wrapper">{children}</div>}>
								<>
									<h4>{t(`${translationRoute}: tracks.Uncategorized`, 'Uncategorized')}</h4>
									{isEditor ? <AssignTracks isOpen={open => setBlockClose?.(open)} /> : null}
								</>
							</ConditionalWrapper>
						) : (
							t(`${translationRoute}:tracks.${track}`, track)
						)}

						<div className='carousel-controls'>
							{/* Shows the pages progress bar */}
							<div ref={progressBarSliderRef} className='progress-bar-slider' style={{ width: `${PROGRESS_BAR_SLIDER_WIDTH}px` }}>
								<div ref={progressBarMarkerRef} className='progress-bar-marker'>
								</div>
							</div>

							{carouselControls}
						</div>
					</div>
				)}

				{/* Carousel */}
				<div ref={ondemandCarouselContainer} className="ondemand-carousel-container">
					<div className="slider" data-slider-index={'0'}>
						<div ref={leftOverflowShadow} className="overflow-shadow overflow-shadow-left" />
						<button ref={leftHandleRef} className='handle left-handle' onClick={(e) => { onHandleClick(e as unknown as ButtonInterface); }}>
							<Icon name={ICONS.ANGLE_LEFT} color="covfefe" size={20} />
						</button>

						<div ref={rightOverflowShadow} className="overflow-shadow overflow-shadow-right" />
						<button ref={rightHandleRef} className="handle right-handle" onClick={(e) => { onHandleClick(e as unknown as ButtonInterface); }}>
							<Icon name={ICONS.ANGLE_RIGHT} color="covfefe" size={20} />
						</button>

						{children}
					</div>
				</div>
			</div>
		</div>
	);
};

